import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll on client-side route changes. SPA navigation keeps the previous
 * page's scroll position; this sends each new page to the top — unless the URL
 * carries a hash, in which case it honors the anchor. In-page anchor clicks
 * (plain <a href="#...">) are handled natively by the browser and don't trigger
 * this (no pathname change).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  // Take over scroll handling from the browser (it would otherwise try to
  // restore a position on back/forward and fight this reset).
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    // Instant jump on route change — the global `scroll-behavior: smooth`
    // (kept for in-page anchor links) would otherwise animate the reset, which
    // reads as the page briefly "remembering" the old scroll position.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  return null;
}
